// Policies & Governance — single source of truth.
// Each policy = { slug, title, eyebrow, summary, body[] } where body[] is a
// list of paragraphs / bullet groups. Rendered by /app/frontend/src/pages/Policies.jsx.

export const SAFEGUARDING_EMAIL = "safeguarding@templeofkulture.co.uk";
export const PRIVACY_EMAIL = "privacy@templeofkulture.co.uk";
export const GENERAL_EMAIL = "info@templeofkulture.co.uk";

export const POLICY_GROUPS = [
  {
    label: "Safeguarding & Wellbeing",
    slugs: [
      "safeguarding",
      "child-protection",
      "online-safety",
      "code-of-conduct",
      "photography-media",
    ],
  },
  {
    label: "Inclusion & Access",
    slugs: ["edi", "accessibility", "immersive-safety"],
  },
  {
    label: "Health, Safety & Operations",
    slugs: ["health-safety", "volunteer", "recruitment", "environmental"],
  },
  {
    label: "Privacy, Data & Money",
    slugs: ["gdpr-privacy", "cookies", "terms", "refund-cancellation", "complaints"],
  },
  {
    label: "Governance",
    slugs: ["financial-procedures", "trustee-conflict"],
  },
];

export const POLICIES = [
  {
    slug: "safeguarding",
    title: "Safeguarding Policy",
    eyebrow: "Protecting everyone in the Temple",
    summary:
      "Everyone who enters Temple of Kulture — children, young people, adults at risk, artists, staff and audiences — has the right to be safe, respected and protected from harm.",
    body: [
      { h: "Our commitment", p: "We safeguard the welfare of all participants in our programmes, retreats, immersive experiences and online activity. Safeguarding is everyone's responsibility — staff, freelancers, volunteers, partners and trustees." },
      { h: "What this means in practice", list: [
        "A designated Safeguarding Lead and Deputy oversee every programme.",
        "All staff and regular volunteers complete safeguarding training annually.",
        "Enhanced DBS checks for all staff and volunteers working with under-18s or adults at risk.",
        "Clear reporting routes for concerns — internal, external and to statutory partners (Local Authority Designated Officer / NSPCC / Police / NHS where relevant).",
        "Confidential record keeping, secure storage and proportionate information sharing.",
      ]},
      { h: "Speak to us", p: `If you have a concern about a child, young person or adult at risk, please contact our Safeguarding Lead in confidence at ${SAFEGUARDING_EMAIL}. If someone is in immediate danger, call 999.` },
    ],
  },
  {
    slug: "child-protection",
    title: "Child Protection Policy",
    eyebrow: "Children & young people under 18",
    summary:
      "We work with children and young people aged 4–25 across our Interactive Playground, Home Education, LEVEL UP and family programmes. Their safety, voice and wellbeing sit at the centre of every decision.",
    body: [
      { h: "Standards we follow", p: "Our practice is aligned with Working Together to Safeguard Children, Keeping Children Safe in Education guidance and Arts Council England safeguarding expectations." },
      { h: "Minimum requirements", list: [
        "Enhanced DBS checks for everyone working unsupervised with under-18s.",
        "1:8 ratio for under-12 sessions, plus a session lead. Higher ratios for Reception-age.",
        "Two adults present in any space with under-18s wherever practical.",
        "Parental / guardian consent for participation, photography and trips.",
        "Risk assessments for every session, venue and off-site activity.",
      ]},
      { h: "Listening to children", p: "Young people are involved in shaping our programmes through feedback circles, youth panel input and post-session check-ins. We act on what they tell us." },
      { h: "Reporting a concern", p: `Contact ${SAFEGUARDING_EMAIL} or, in an emergency, call 999 / the NSPCC on 0808 800 5000.` },
    ],
  },
  {
    slug: "edi",
    title: "Equality, Diversity & Inclusion Policy",
    eyebrow: "Built by and for the global majority",
    summary:
      "Temple of Kulture exists to platform global majority stories. Equality, diversity and inclusion are not a section of our work — they are the work.",
    body: [
      { h: "What we commit to", list: [
        "Centring global majority artists, technologists and audiences in every commission, programme and hire.",
        "Removing financial, geographical and cultural barriers to access — funded places, transport support and language access.",
        "Intersectional practice across race, disability, neurodivergence, sexuality, gender, faith, age and class.",
        "Inclusive recruitment, mentorship and progression routes into the creative and immersive technology industries.",
      ]},
      { h: "Holding ourselves accountable", p: "We monitor participation, leadership representation and pay equity annually and publish a summary as part of our public Impact Report." },
    ],
  },
  {
    slug: "accessibility",
    title: "Accessibility Statement",
    eyebrow: "Designed to be opened, not navigated around",
    summary:
      "We design our digital and live experiences so that as many people as possible can step inside — including disabled, Deaf, neurodivergent and chronically ill participants.",
    body: [
      { h: "Live & immersive experiences", list: [
        "Step-free access at all main venues, including HYPHA Studio (Chatham) and partner sites where possible.",
        "Relaxed performance options on flagship runs.",
        "Captioning, BSL interpretation and audio description on selected shows — see individual event pages.",
        "Sensory-aware sessions, content notes ahead of immersive experiences and visual stories on request.",
        "Disabled access companion tickets available — email us before booking.",
      ]},
      { h: "Digital", list: [
        "Mobile-first responsive design, semantic HTML and visible focus states.",
        "We aim for WCAG 2.2 AA across new pages and are progressively remediating older surfaces.",
        "Alternative text on key imagery and keyboard-navigable interactive elements.",
      ]},
      { h: "Tell us how we can do better", p: `Email ${GENERAL_EMAIL} with the subject line "Accessibility" — we will reply within 5 working days.` },
    ],
  },
  {
    slug: "immersive-safety",
    title: "Immersive Experience Safety & Accessibility",
    eyebrow: "Step inside, safely",
    summary:
      "Our immersive experiences use VR, projection mapping, lighting, sound design and interactive environments. We want everyone to enjoy them — here is what to expect and how we keep you safe.",
    body: [
      { h: "What to expect", list: [
        "Low lighting, atmospheric soundscapes, projection imagery and moving visuals.",
        "Some experiences include haze, scent design, flashing lights or strobe-style effects — these are signposted on each event page.",
        "VR / XR experiences involve wearing a headset and limited movement within a defined safe space.",
        "Interactive Playground and walking experiences involve gentle physical movement.",
      ]},
      { h: "Photosensitivity & sensory considerations", p: "Some experiences contain flashing lights, strobe effects or rapid imagery and may not be suitable for people with photosensitive epilepsy, migraine or sensory sensitivities. Specific warnings appear on each show's page. If you are unsure, please email us before booking." },
      { h: "VR & XR wellbeing", list: [
        "Headsets are sanitised between every user with industry-standard wipes. Disposable face liners are available.",
        "Recommended minimum age for headset-based VR is 13 (manufacturer guidance).",
        "VR can occasionally cause dizziness, nausea or disorientation. You can stop at any time — staff are present throughout.",
        "Pregnant participants, anyone with inner-ear conditions or those feeling unwell are advised against headset use.",
      ]},
      { h: "Physical, sensory & medical needs", list: [
        "Please let staff know about any accessibility, sensory or medical needs in advance or on arrival. We will adjust where we can.",
        "Breaks, quieter spaces and exits are available at any time — just ask any team member.",
        "Under-16s must be accompanied for most immersive experiences. Specific age guidance is listed on each event page.",
      ]},
      { h: "Off-site activations & public liability", p: "Our pop-up and off-site activations are covered by full public liability insurance. Venue-specific risk assessments are available on request." },
      { h: "Reassurance, not red tape", p: "These notes exist to help you choose what is right for you — not to put you off. If you have questions before booking, email " + GENERAL_EMAIL + " and we'll talk you through it." },
    ],
  },
  {
    slug: "health-safety",
    title: "Health & Safety Policy",
    eyebrow: "Practical, proportionate, present",
    summary:
      "We provide safe spaces for participants, audiences, artists, freelancers and staff at every Temple of Kulture activity.",
    body: [
      { h: "Standards", list: [
        "Compliance with the Health & Safety at Work Act 1974 and venue-specific regulations.",
        "Site-specific risk assessments for every public event, immersive experience and off-site activation.",
        "First-aid trained staff present at all live events.",
        "Fire, evacuation and incident procedures rehearsed and signposted.",
        "Annual review of equipment, electrical safety (PAT) and rigging.",
      ]},
      { h: "Incident reporting", p: "All incidents and near-misses are logged and reviewed. RIDDOR-reportable incidents are reported within statutory timeframes." },
    ],
  },
  {
    slug: "gdpr-privacy",
    title: "GDPR / Privacy Policy",
    eyebrow: "Your data, on your terms",
    summary:
      "Temple of Kulture is the data controller for personal data collected via this website, our forms and our programmes. We hold your information lawfully, fairly and only for as long as we need it.",
    body: [
      { h: "What we collect", list: [
        "Identity & contact information: name, email, phone, postcode/location, age range where relevant.",
        "Programme-specific data: interests, referral source, application content, accessibility needs.",
        "Operational data: booking history, payment receipts (we never store full card numbers — Stripe handles payment data directly).",
        "Anonymous device data: device type, browser, language, page source — used to improve the site and signpost the right pathways.",
      ]},
      { h: "Why we hold it", p: "To deliver experiences, programmes and bookings you have asked for; to keep you safe at our events; to send updates you have opted into; and to evaluate our impact for funders and partners." },
      { h: "Your rights", list: [
        "Access — request a copy of the data we hold about you.",
        "Rectification — ask us to correct anything inaccurate.",
        "Erasure — ask us to delete your data (subject to lawful retention).",
        "Portability and objection rights as set out by the UK GDPR.",
      ]},
      { h: "Marketing consent", p: "We never add anyone to our mailing list without an explicit sign-up. Every email contains an unsubscribe link." },
      { h: "Contact our data team", p: `Email ${PRIVACY_EMAIL}. You also have the right to complain to the Information Commissioner's Office (ICO).` },
    ],
  },
  {
    slug: "cookies",
    title: "Cookies Policy",
    eyebrow: "What we set and why",
    summary:
      "We use a small number of cookies to keep the site working and to understand which experiences resonate. We do not sell your data.",
    body: [
      { h: "Essential", p: "Strictly necessary cookies that enable basic site functionality (e.g. the portal intro flag we store locally to avoid replaying it). These do not require consent under PECR." },
      { h: "Analytics", p: "Anonymised analytics help us understand which pages and pathways serve our community best. You can disable these in your browser settings at any time." },
      { h: "No advertising trackers", p: "We do not run third-party advertising trackers or sell audience data to ad networks." },
    ],
  },
  {
    slug: "terms",
    title: "Terms & Conditions",
    eyebrow: "Booking with us",
    summary:
      "These terms apply when you book an experience, register for a programme, donate or otherwise engage with Temple of Kulture.",
    body: [
      { h: "Bookings", list: [
        "Tickets are issued upon successful payment. Please bring your confirmation email (digital is fine).",
        "Age restrictions, late-entry rules and content notes appear on each event page — please read these before booking.",
        "We reserve the right to refuse entry for safeguarding, safety or behavioural reasons consistent with our Code of Conduct.",
      ]},
      { h: "Programme participation", p: "Programme places (e.g. LEVEL UP, Home Education) are awarded after application, interview and / or referral. Acceptance is confirmed in writing." },
      { h: "Intellectual property", p: "All written, visual and audio content on this site, including bespoke imagery, remains the property of Temple of Kulture or its named creators." },
    ],
  },
  {
    slug: "refund-cancellation",
    title: "Refund & Cancellation Policy",
    eyebrow: "Fair, clear and human",
    summary:
      "We know plans change. Here is exactly how we handle changes, cancellations and refunds.",
    body: [
      { h: "Customer cancellations", list: [
        "Refunds available up to 7 days before the event for ticketed experiences (less payment processing fees).",
        "Within 7 days: we will try to transfer you to an alternative date wherever possible.",
        "Programme deposits (LEVEL UP, retreats) are detailed in your acceptance pack.",
      ]},
      { h: "If we cancel or reschedule", p: "We will offer a full refund or a free transfer to a future date. We will let you know as soon as possible by email and SMS where provided." },
      { h: "Donations", p: "Donations are non-refundable unless we hear from you within 14 days of the contribution and the contribution has not yet been spent." },
      { h: "How to request a refund", p: `Email ${GENERAL_EMAIL} with your booking reference. We aim to process refunds within 10 working days.` },
    ],
  },
  {
    slug: "code-of-conduct",
    title: "Code of Conduct",
    eyebrow: "How we move in the Temple",
    summary:
      "Temple of Kulture is a brave, joyful, accountable space. Everyone — audience, artist, staff, partner — agrees to the same standards.",
    body: [
      { h: "We expect", list: [
        "Respect for one another's identity, body, story and boundaries.",
        "Active anti-racism, anti-ableism and anti-discrimination — including in language, gesture and humour.",
        "Consent before contact, photography or recording.",
        "Sober, safe behaviour during all activities involving children, young people or adults at risk.",
      ]},
      { h: "We do not tolerate", list: [
        "Harassment, bullying, intimidation, hate speech or violence in any form, online or in person.",
        "Discrimination on any protected characteristic.",
        "Misuse of immersive environments — including non-consensual capture of other participants.",
      ]},
      { h: "If something happens", p: "Speak to any team member wearing a TOK lanyard, or email " + GENERAL_EMAIL + ". We take every report seriously and respond within 3 working days." },
    ],
  },
  {
    slug: "online-safety",
    title: "Online Safety / Digital Safety Policy",
    eyebrow: "Safe online, safe in the metaverse",
    summary:
      "Our digital and XR spaces are held to the same safeguarding standards as our live spaces.",
    body: [
      { h: "What we do", list: [
        "Moderated online community channels — public Discord / Slack / forum spaces (where we run them) are staffed and rules are visible.",
        "Verified accounts for any direct messaging with under-18s; communication routed through parent/guardian channels by default.",
        "Privacy-aware defaults in our XR spaces — voice and avatar muting tools, blocking and reporting.",
        "Education on digital wellbeing, deepfakes, AI consent and online identity within LEVEL UP and Home Education.",
      ]},
      { h: "AI-generated content", p: "We use AI tools (image, video and text) as part of our creative practice. We disclose when content is AI-generated, do not use minors' likenesses to train models, and apply human review on every public output." },
    ],
  },
  {
    slug: "complaints",
    title: "Complaints Policy",
    eyebrow: "We want to hear it",
    summary:
      "If something has gone wrong, please tell us. We treat every complaint as a chance to be better.",
    body: [
      { h: "How to complain", list: [
        `Email ${GENERAL_EMAIL} with the subject "Complaint" and any details you are comfortable sharing.`,
        "We will acknowledge receipt within 3 working days.",
        "We aim to give a substantive response within 15 working days. Complex cases may take longer — we will tell you why and keep you updated.",
      ]},
      { h: "If you are not satisfied", p: "You can escalate to our Board of Trustees via the same email address, or to relevant external regulators (e.g. the Charity Commission, ICO or Arts Council England)." },
    ],
  },
  {
    slug: "environmental",
    title: "Environmental & Sustainability Statement",
    eyebrow: "Cinematic doesn't have to mean wasteful",
    summary:
      "Our work uses screens, projection, lighting and travel. We take responsibility for the footprint of those choices.",
    body: [
      { h: "What we commit to", list: [
        "Low-power LED lighting and projection wherever technically possible.",
        "Reusable set, costume and signage assets across productions.",
        "Train-first travel policy for UK touring; carbon offsetting on essential flights.",
        "Local sourcing for food, drink and merchandise at festivals and retreats.",
        "Annual review of our environmental footprint with an impact summary published with our funders.",
      ]},
    ],
  },
  {
    slug: "trustee-conflict",
    title: "Trustee Conflict of Interest Policy",
    eyebrow: "Open governance",
    summary:
      "Our Board operates with full transparency about any actual, potential or perceived conflicts of interest.",
    body: [
      { h: "Standards", list: [
        "All trustees and senior staff complete an annual declaration of interests.",
        "Conflicted parties withdraw from decisions in which they have a personal, professional or financial interest.",
        "Declarations are minuted at every Board meeting and reviewed at AGM.",
      ]},
    ],
  },
  {
    slug: "financial-procedures",
    title: "Financial Procedures Policy",
    eyebrow: "Funds handled with care",
    summary:
      "Our financial procedures are designed to keep public money, grant funding and audience contributions safe and accountable.",
    body: [
      { h: "Controls", list: [
        "Dual authorisation on all payments above an agreed threshold.",
        "Monthly bank reconciliation and quarterly management accounts.",
        "Independent annual examination / audit of accounts, depending on income threshold.",
        "Restricted-fund tracking so grant funding is spent exactly as intended.",
        "Anti-fraud, anti-bribery and whistleblowing routes available to all staff.",
      ]},
    ],
  },
  {
    slug: "photography-media",
    title: "Photography & Media Consent Policy",
    eyebrow: "Your face, your call",
    summary:
      "We photograph and film our work for documentation, marketing, funder reporting and storytelling. Your consent is always required.",
    body: [
      { h: "How consent works", list: [
        "Adults sign a clear media consent form, with the option to opt out at any time.",
        "Under-18s require parental/guardian consent. We re-confirm consent annually for ongoing participants.",
        "Where filming or photography is taking place, the area is signposted and a non-filming zone is provided.",
        "Where someone withdraws consent, we will remove their image from public-facing channels within 30 days.",
      ]},
    ],
  },
  {
    slug: "volunteer",
    title: "Volunteer Policy",
    eyebrow: "Volunteers held in the same care as staff",
    summary:
      "Volunteers are part of the Temple. They are inducted, supported, valued and protected to the same standards as paid staff.",
    body: [
      { h: "What volunteers receive", list: [
        "A formal induction including safeguarding, code of conduct and immersive safety briefing.",
        "Expenses reimbursed in line with our published expenses guidance.",
        "Recognition routes — references, skill-sharing sessions and progression into paid roles where roles arise.",
        "A named supervisor for every volunteer placement.",
      ]},
    ],
  },
  {
    slug: "recruitment",
    title: "Recruitment & Safer Hiring Policy",
    eyebrow: "Safer hiring, fairer hiring",
    summary:
      "We recruit openly, fairly and with safeguarding baked in.",
    body: [
      { h: "Standards", list: [
        "Open advertising of all paid roles; targeted outreach to global majority and disabled creative networks.",
        "Structured shortlisting and interviews, two-person panels and consistent scoring rubrics.",
        "References and enhanced DBS checks for all roles involving under-18s or adults at risk.",
        "Transparent pay bands and a published commitment to the Living Wage.",
      ]},
    ],
  },
];

export const POLICY_INDEX = Object.fromEntries(POLICIES.map((p) => [p.slug, p]));

export function getPolicy(slug) {
  return POLICY_INDEX[slug] || null;
}
